import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  createQuickTask,
  searchQuickTaskAdminAssignees,
  searchQuickTaskArtistAssignees,
} from '../../../api/quickTasks.ts';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import PersonAvatar from '../../common/avatar/PersonAvatar';
import '../../../css/admin/tasks/QuickTaskModal.css';

const PRIORITY_OPTIONS = [
  { value: 'normal', label: '通常' },
  { value: 'high', label: '高' },
  { value: 'urgent', label: '緊急' },
];

const SOURCE_CONTEXT_LABELS = {
  event: 'イベント詳細に紐づけ',
  artist: 'アーティスト詳細に紐づけ',
  chat_artist: 'アーティストチャットに紐づけ',
  chat_event: 'イベントチャットに紐づけ',
  chat_group: 'グループチャットに紐づけ',
  contract: '本契約に紐づけ',
  offer: 'オファーに紐づけ',
};

const compactText = (value, max = 80) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const normalizeInitialMemo = (value) => {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  return String(value).trim().slice(0, 5000);
};

const normalizeInitialDueOn = (value) => {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const text = String(value).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return '';
  const [year, month, day] = text.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
    ? text
    : '';
};

const getSourceContext = (source) => {
  if (!source || typeof source !== 'object') return null;
  const type = String(source.type || '').trim();
  const title = compactText(source.title || source.name || '', 80);
  const excerpt = compactText(source.excerpt || source.messageText || '', 100);
  if (!type || type === 'manual') return null;
  return {
    label: SOURCE_CONTEXT_LABELS[type] || '関連情報に紐づけ',
    title,
    excerpt,
  };
};

const getSourceResetKey = (source) => {
  if (!source || typeof source !== 'object') return '';
  try {
    return JSON.stringify(source);
  } catch {
    return String(source?.type || '');
  }
};

const pad = (n) => String(n).padStart(2, '0');

const toDateValue = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const addDaysDate = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toDateValue(d);
};

const formatDateLabel = (value) => {
  const text = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return '';
  const [, m, d] = text.split('-');
  return `${Number(m)}/${Number(d)}`;
};

const normalizeAdminCandidate = (item) => {
  const id = Number(item?.adminUserId || item?.admin_user_id || item?.id || 0);
  if (!Number.isInteger(id) || id <= 0) return null;
  const label = String(item?.displayName || item?.display_name || item?.fullName || item?.full_name || item?.username || '').trim() || `管理者 ${id}`;
  const subLabel = [item?.username, item?.email].map((v) => String(v || '').trim()).filter(Boolean).join(' / ');
  return {
    type: 'admin',
    id,
    label,
    subLabel,
    profilePicture: String(item?.profilePicture || item?.profile_picture || '').trim(),
  };
};

const normalizeArtistCandidate = (item) => {
  const id = String(item?.artist_id || item?.artistId || '').trim();
  if (!id) return null;
  const label = String(item?.name || item?.name_en || '').trim() || 'アーティスト';
  const subLabel = [item?.name_en, item?.email || item?.provisional_email]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(' / ');
  return {
    type: 'artist',
    id,
    label,
    subLabel,
    gender: String(item?.gender || item?.artistGender || item?.artist_gender || '').trim(),
    profilePicture: String(item?.profilePicture || item?.profile_picture || item?.profile_picture_url || '').trim(),
  };
};

const AssigneeAvatar = ({ item, className = '' }) => {
  const label = item?.label || '担当者';
  return (
    <PersonAvatar
      person={item}
      role={item?.type}
      gender={item?.gender}
      name={label}
      className={`qtp__avatar ${className}`.trim()}
      title={label}
      size={34}
      ariaHidden
    />
  );
};

// 依頼先を選ぶ独立モーダル（コンポーザーの上に重ねて表示）
function AssigneePickerModal({ open, current, onSelect, onSelf, onClose }) {
  const [pickerType, setPickerType] = useState('admin');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setPickerType(current?.type || 'admin');
    setQuery('');
    setItems([]);
    setError('');
  }, [open, current]);

  useEffect(() => {
    if (!open) return undefined;
    let ignore = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const rawItems = pickerType === 'artist'
          ? await searchQuickTaskArtistAssignees(query)
          : await searchQuickTaskAdminAssignees(query);
        const normalized = rawItems
          .map(pickerType === 'artist' ? normalizeArtistCandidate : normalizeAdminCandidate)
          .filter(Boolean);
        if (!ignore) setItems(normalized);
      } catch (err) {
        if (!ignore) {
          setItems([]);
          setError(err?.message || '候補の取得に失敗しました。');
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }, 220);
    return () => {
      ignore = true;
      window.clearTimeout(timer);
    };
  }, [open, query, pickerType]);

  if (!open) return null;

  return (
    <DsModal open={open} onClose={onClose} title="依頼先を選択" size="sm" className="quick-task-picker-modal">
      <p className="qtp__lead">タスクを担当する1人を選びます。</p>

      <button type="button" className={`qtp__self ${!current ? 'is-current' : ''}`} onClick={onSelf}>
        <AssigneeAvatar item={{ type: 'admin', label: '自分' }} />
        <span className="qtp__selfText">
          <strong>自分</strong>
          <small>自分のタスクにする</small>
        </span>
        {!current ? <i className="fa-solid fa-check qtp__currentMark" aria-hidden="true" /> : null}
      </button>

      <div className="qtp__tabs" role="tablist">
        <button type="button" className={pickerType === 'admin' ? 'is-active' : ''} onClick={() => setPickerType('admin')}>管理者</button>
        <button type="button" className={pickerType === 'artist' ? 'is-active' : ''} onClick={() => setPickerType('artist')}>アーティスト</button>
      </div>

      <div className="qtp__searchRow">
        <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={pickerType === 'artist' ? '名前・英語名・メールで検索' : '名前・ユーザー名・メールで検索'}
          autoFocus
        />
      </div>

      {error ? <div className="qtc-error qtc-error--inline">{error}</div> : null}

      <div className="qtp__list">
        {loading ? (
          <div className="qtp__empty">検索中…</div>
        ) : items.length ? (
          items.map((item) => {
            const isCurrent = current && current.type === item.type && String(current.id) === String(item.id);
            return (
              <button
                type="button"
                key={`${item.type}:${item.id}`}
                className={`qtp__item ${isCurrent ? 'is-current' : ''}`}
                onClick={() => onSelect(item)}
              >
                <AssigneeAvatar item={item} />
                <span className="qtp__itemText">
                  <strong>{item.label}</strong>
                  {item.subLabel ? <small>{item.subLabel}</small> : null}
                </span>
                {isCurrent ? <i className="fa-solid fa-check qtp__currentMark" aria-hidden="true" /> : null}
              </button>
            );
          })
        ) : (
          <div className="qtp__empty">候補がありません。</div>
        )}
      </div>
    </DsModal>
  );
}

function QuickTaskTitle() {
  return (
    <span className="qtc-modal-title">
      <span>クイックタスク</span>
      <span className="qtc__hint">Enter / ⌘+Enter / Ctrl+Enter で作成</span>
    </span>
  );
}

const isImeComposingEvent = (event) => Boolean(
  event?.isComposing
    || event?.nativeEvent?.isComposing
    || event?.keyCode === 229
    || event?.which === 229
    || event?.nativeEvent?.keyCode === 229
    || event?.nativeEvent?.which === 229,
);

export default function QuickTaskModal({
  open,
  source,
  initialTitle = '',
  initialMemo = '',
  initialDueOn = '',
  onClose,
  onCreated,
}) {
  const sourceResetKey = useMemo(() => getSourceResetKey(source), [source]);
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const [title, setTitle] = useState(() => compactText(initialTitle));
  const [memo, setMemo] = useState(() => normalizeInitialMemo(initialMemo));
  const [priority, setPriority] = useState('normal');
  const [dueOn, setDueOn] = useState(() => normalizeInitialDueOn(initialDueOn));
  const [assignee, setAssignee] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const titleRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTitle(compactText(initialTitle));
    setMemo(normalizeInitialMemo(initialMemo));
    setPriority('normal');
    setDueOn(normalizeInitialDueOn(initialDueOn));
    setAssignee(null);
    setPickerOpen(false);
    setSaving(false);
    setError('');
  }, [open, sourceResetKey, initialDueOn, initialMemo, initialTitle]);

  if (!open) return null;

  const submit = async () => {
    if (saving) return;
    const safeTitle = title.trim();
    const isArtistRequest = assignee?.type === 'artist';
    if (!safeTitle) {
      setError(isArtistRequest ? '依頼内容を入力してください。' : 'タイトルを入力してください。');
      titleRef.current?.focus();
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: safeTitle,
        description: isArtistRequest ? null : memo.trim() || null,
        priority: isArtistRequest ? 'normal' : priority,
        dueOn: dueOn || null,
        assigneeIds: assignee?.type === 'admin' ? [assignee.id] : [],
        artistAssigneeIds: assignee?.type === 'artist' ? [assignee.id] : [],
        source: isArtistRequest ? { type: 'manual' } : source,
        sourceType: isArtistRequest ? 'manual' : source?.type,
        eventUuid: isArtistRequest ? null : source?.eventUuid || null,
        artistId: isArtistRequest ? null : source?.artistId || null,
      };
      const result = await createQuickTask(payload);
      onCreated?.(result);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'クイックタスクの作成に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleFormKeyDown = (e) => {
    if (isImeComposingEvent(e)) return;
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  const handleTitleKeyDown = (e) => {
    if (isImeComposingEvent(e)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  const dueChoices = [
    { label: '今日', value: addDaysDate(0) },
    { label: '明日', value: addDaysDate(1) },
    { label: '来週', value: addDaysDate(7) },
  ];
  const isCustomDate = dueOn && !dueChoices.some((c) => c.value === dueOn);

  const assigneeTypeLabel = assignee ? (assignee.type === 'artist' ? 'アーティストに依頼' : '管理者に依頼') : '自分のタスク';
  const isArtistRequest = assignee?.type === 'artist';
  const sourceContext = getSourceContext(source);

  return (
    <>
      <DsModal
        open={open}
        onClose={onClose}
        title={<QuickTaskTitle />}
        size="sm"
        dismissible={!saving && !pickerOpen}
        className="quick-task-modal"
        footer={(
          <DsButton block onClick={submit} loading={saving}>
            {saving ? '作成中…' : 'タスクを作成'}
          </DsButton>
        )}
        onKeyDown={handleFormKeyDown}
      >
        <div className="qtc__body">
          <input
            ref={titleRef}
            className="qtc__titleInput"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleTitleKeyDown}
            placeholder={isArtistRequest ? '依頼内容を入力' : '何をする？'}
            autoFocus
          />
          {!isArtistRequest ? (
            <input
              className="qtc__memoInput"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="メモを追加（任意）"
            />
          ) : null}

          {!isArtistRequest && sourceContext ? (
            <div className="qtc__source">
              <i className="fa-solid fa-link" aria-hidden="true" />
              <span>
                <strong>{sourceContext.title || sourceContext.label}</strong>
                {sourceContext.excerpt ? <span>{sourceContext.excerpt}</span> : null}
              </span>
            </div>
          ) : null}

          {/* 担当 */}
          <section className="qtc-field">
            <span className="qtc-field__label">担当</span>
            <button type="button" className="qtc-assignee" onClick={() => setPickerOpen(true)}>
              <PersonAvatar
                person={assignee || { type: 'admin', label: '自分' }}
                role={assignee?.type || 'admin'}
                name={assignee?.label || '自分'}
                className={`qtc-assignee__avatar ${assignee ? `is-${assignee.type}` : 'is-self'}`}
                fallback="自"
                size={38}
                ariaHidden
              />
              <span className="qtc-assignee__text">
                <strong>{assignee ? assignee.label : '自分'}</strong>
                <small>{assigneeTypeLabel}</small>
              </span>
              <span className="qtc-assignee__action">{assignee ? '変更' : '選択'}</span>
            </button>
          </section>

          {/* 期限 */}
          <section className="qtc-field">
            <span className="qtc-field__label">期限</span>
            <div className="qtc-due">
              <div className="qtc-due__quick">
                {dueChoices.map((choice) => (
                  <button
                    key={choice.value}
                    type="button"
                    className={`qtc-pill ${dueOn === choice.value ? 'is-active' : ''}`}
                    onClick={() => setDueOn((prev) => (prev === choice.value ? '' : choice.value))}
                  >
                    {choice.label}
                  </button>
                ))}
                <label className={`qtc-pill qtc-pill--date ${isCustomDate ? 'is-active' : ''}`}>
                  <i className="fa-solid fa-calendar-day" aria-hidden="true" />
                  <span>{isCustomDate ? formatDateLabel(dueOn) : '日付を選択'}</span>
                  <input
                    type="date"
                    className="qtc-pill__dateInput"
                    value={dueOn}
                    onChange={(e) => setDueOn(e.target.value)}
                  />
                </label>
                {dueOn ? (
                  <button type="button" className="qtc-pill qtc-pill--clear" onClick={() => setDueOn('')}>
                    <i className="fa-solid fa-xmark" aria-hidden="true" /> クリア
                  </button>
                ) : null}
              </div>
            </div>
          </section>

          {/* 優先度 */}
          {!isArtistRequest ? (
            <section className="qtc-field">
              <span className="qtc-field__label">優先度</span>
              <div className="qtc-seg" role="radiogroup" aria-label="優先度">
                {PRIORITY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={priority === option.value}
                    className={`qtc-seg__btn is-${option.value} ${priority === option.value ? 'is-active' : ''}`}
                    onClick={() => setPriority(option.value)}
                  >
                    <span className="qtc-seg__dot" aria-hidden="true" />
                    {option.label}
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {error ? <div className="qtc-error">{error}</div> : null}

          <p className="qtc__shortcutGuide">
            モーダル起動: Mac <kbd>⌘</kbd> + <kbd>Option</kbd> + <kbd>T</kbd> / Windows <kbd>Ctrl</kbd> + <kbd>Alt</kbd> + <kbd>T</kbd>
          </p>
        </div>

      </DsModal>

      <AssigneePickerModal
        open={pickerOpen}
        current={assignee}
        onClose={() => setPickerOpen(false)}
        onSelf={() => { setAssignee(null); setPickerOpen(false); }}
        onSelect={(item) => { setAssignee(item); setPickerOpen(false); }}
      />
    </>
  );
}
